/**
 * Chip — removable tag/filter chip component.
 *
 * Replaces MUI Chip for filter tags, multi-select values, and removable
 * labels. Uses the same CSS variable pattern as Badge in Primitives.tsx.
 *
 * Usage:
 *   <Chip>Default</Chip>
 *   <Chip tone="success">Active</Chip>
 *   <Chip tone="warning" onRemove={() => handleRemove(id)}>Pending</Chip>
 *   <Chip tone="info" onClick={() => toggleFilter()} size="md">Payroll</Chip>
 *   <Chip bg="#e0f0ff" fg="#004080">Custom Color</Chip>
 *   <Chip onRemove={drop} removeLabel={`Remove ${name}`}>{name}</Chip>
 */

import React from 'react'
import styled, {css} from 'styled-components'

export type ChipTone = 'success' | 'warning' | 'error' | 'info' | 'pending' | 'neutral'

export interface ChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  children: React.ReactNode
  /** Semantic color tone. Default: 'neutral' */
  tone?: ChipTone
  /** When provided, renders an X remove button on the right */
  onRemove?: () => void
  /**
   * Accessible name of the remove button. Default: 'Remove'. Where several chips sit together,
   * name the thing being removed (`Remove ${name}`) so the buttons are not all called the same.
   */
  removeLabel?: string
  /** Extra attributes for the remove button: a test id, `tabIndex`, key handlers. */
  removeButtonProps?: Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'type' | 'onClick' | 'disabled' | 'aria-label'
  > & {[dataAttribute: `data-${string}`]: string | undefined}
  /** When provided, adds cursor:pointer and role="button" */
  onClick?: () => void
  disabled?: boolean
  /** Size variant. Default: 'sm' */
  size?: 'sm' | 'md'
  /** Custom background color — overrides tone when provided */
  bg?: string
  /** Custom text color — overrides tone when provided */
  fg?: string
  'data-testid'?: string
}

/* -----------------------------------------------------------------------
   Tone styles — mirrors badgeVariantStyles in Primitives.tsx
   --------------------------------------------------------------------- */

const TONE_STYLES: Record<ChipTone, ReturnType<typeof css>> = {
  success: css`
    background: var(--success-100);
    color: var(--success-800);
  `,
  warning: css`
    background: var(--warning-100);
    color: var(--warning-800);
  `,
  error: css`
    background: var(--error-100);
    color: var(--error-800);
  `,
  info: css`
    background: var(--info-100);
    color: var(--info-800);
  `,
  pending: css`
    background: var(--pending-100);
    color: var(--pending-800);
  `,
  neutral: css`
    background: var(--neutral-200);
    color: var(--neutral-800);
  `,
}

/* -----------------------------------------------------------------------
   Styled components
   --------------------------------------------------------------------- */

const StyledChip = styled.span<{
  $tone: ChipTone
  $size: 'sm' | 'md'
  $clickable: boolean
  $disabled: boolean
  $bg?: string
  $fg?: string
}>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-weight: var(--fw-medium);
  white-space: nowrap;
  user-select: none;
  border: none;
  outline: none;
  transition: opacity 150ms ease, filter 150ms ease;

  /* Size */
  ${({$size}) =>
    $size === 'sm'
      ? css`
          padding: 2px var(--space-2);
          font-size: var(--fs-caption);
          line-height: 1.4;
        `
      : css`
          padding: var(--space-1) 10px;
          font-size: var(--fs-body);
          line-height: 1.5;
        `}

  /* Tone (applied first; bg/fg overrides take precedence below) */
  ${({$tone}) => TONE_STYLES[$tone]}

  /* Custom color overrides */
  ${({$bg}) => $bg && `background: ${$bg};`}
  ${({$fg}) => $fg && `color: ${$fg};`}

  /* Clickable state */
  ${({$clickable}) =>
    $clickable &&
    css`
      cursor: pointer;
      &:hover:not([aria-disabled='true']) {
        filter: brightness(0.95);
      }
      &:active:not([aria-disabled='true']) {
        filter: brightness(0.9);
      }
      &:focus-visible {
        outline: none;
        box-shadow: var(--focus-ring);
      }
    `}

  /* Disabled state */
  ${({$disabled}) =>
    $disabled &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    `}
`

const RemoveButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  margin-left: 2px;
  cursor: pointer;
  color: inherit;
  opacity: 0.7;
  border-radius: 50%;
  flex-shrink: 0;
  line-height: 0;

  &:hover {
    opacity: 1;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

/* Inline SVG X icon — 12×12 rendered, 16×16 viewBox */
const XIcon = () => (
  <svg width={12} height={12} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
    <line
      x1="3"
      y1="3"
      x2="13"
      y2="13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <line
      x1="13"
      y1="3"
      x2="3"
      y2="13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

/* -----------------------------------------------------------------------
   Component
   --------------------------------------------------------------------- */

export const Chip: React.FC<ChipProps> = ({
  children,
  tone = 'neutral',
  onRemove,
  removeLabel = 'Remove',
  removeButtonProps,
  onClick,
  disabled = false,
  size = 'sm',
  bg,
  fg,
  'data-testid': testId,
  ...rest
}) => {
  const isClickable = Boolean(onClick)

  const handleRemove = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    if (!disabled) {
      onRemove?.()
    }
  }

  return (
    <StyledChip
      $tone={tone}
      $size={size}
      $clickable={isClickable}
      $disabled={disabled}
      $bg={bg}
      $fg={fg}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable && !disabled ? 0 : undefined}
      aria-disabled={disabled || undefined}
      onClick={!disabled ? onClick : undefined}
      data-testid={testId}
      {...rest}>
      {children}
      {onRemove && (
        <RemoveButton
          {...removeButtonProps}
          type="button"
          aria-label={removeLabel}
          disabled={disabled}
          onClick={handleRemove}>
          <XIcon />
        </RemoveButton>
      )}
    </StyledChip>
  )
}

Chip.displayName = 'Chip'

export default Chip
